import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AuthShell } from "@/pages/Login";
import PasswordInput from "@/components/PasswordInput";
import AppleSignInButton from "@/components/AppleSignInButton";
import { isNativeApp, canShowPlans } from "@/lib/platform";
import { getStoredRef, clearStoredRef } from "@/lib/refTracking";
import { trackSignup } from "@/lib/analytics";

export default function Register() {
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const { setUser } = useAuth();
    const nextPath = searchParams.get("next");
    const prefilledEmail = searchParams.get("email") || "";
    const [name, setName] = useState("");
    const [email, setEmail] = useState(prefilledEmail);
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            const signup_ref = getStoredRef() || undefined;
            const { data } = await api.post("/auth/register", { name, email, password, signup_ref });
            if (signup_ref) clearStoredRef();
            setUser(data);
            // Fire the Google Ads signup conversion (once, on first-time
            // account creation — the backend sets is_new_user).
            if (data.is_new_user) trackSignup({ transactionId: data.id });
            // If this is an invite accept flow, send the user back to the invite
            // page (they'll be auto-accepted now that they're signed in).
            nav(nextPath || "/onboarding", { replace: true });
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setBusy(false);
        }
    };

    const googleSignIn = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + (nextPath || "/app");
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <AuthShell
            title="Create your free account"
            subtitle={canShowPlans() ? "7 days of Pro included · no credit card" : "Set sail in under a minute"}
        >
            {!isNativeApp() && (
                <>
                    <button
                        data-testid="register-google-btn"
                        type="button"
                        className="bm-btn bm-btn-ghost"
                        style={{ width: "100%", justifyContent: "center", padding: "12px 16px" }}
                        onClick={googleSignIn}
                    >
                        Continue with Google
                    </button>

                    <div style={{ height: 10 }} />

                    <AppleSignInButton
                        onAuthed={(data) => {
                            setUser(data);
                            nav(nextPath || "/onboarding", { replace: true });
                        }}
                    />

                    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0" }}>
                        <div style={{ flex: 1, borderTop: "1px solid var(--bm-border-soft)" }} />
                        <span style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--bm-text-dim)" }}>or</span>
                        <div style={{ flex: 1, borderTop: "1px solid var(--bm-border-soft)" }} />
                    </div>
                </>
            )}
            {isNativeApp() && (
                <>
                    <AppleSignInButton
                        onAuthed={(data) => {
                            setUser(data);
                            nav(nextPath || "/onboarding", { replace: true });
                        }}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0" }}>
                        <div style={{ flex: 1, borderTop: "1px solid var(--bm-border-soft)" }} />
                        <span style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--bm-text-dim)" }}>or</span>
                        <div style={{ flex: 1, borderTop: "1px solid var(--bm-border-soft)" }} />
                    </div>
                </>
            )}

            <form onSubmit={submit} data-testid="register-form">
                <div style={{ marginBottom: 14 }}>
                    <label className="bm-label">Your name</label>
                    <input data-testid="register-name-input" className="bm-input" value={name} onChange={(e) => setName(e.target.value)} required minLength={1} maxLength={80} placeholder="Captain Smith" />
                </div>
                <div style={{ marginBottom: 14 }}>
                    <label className="bm-label">Email</label>
                    <input data-testid="register-email-input" type="email" className="bm-input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
                </div>
                <div style={{ marginBottom: 18 }}>
                    <label className="bm-label">Password (min 8 characters)</label>
                    <PasswordInput data-testid="register-password-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required minLength={8} />
                </div>
                {err && (
                    <div data-testid="register-error" style={{ marginBottom: 14, color: "var(--bm-bad)", fontSize: 13 }}>{err}</div>
                )}
                <button data-testid="register-submit-btn" type="submit" disabled={busy} className="bm-btn bm-btn-gold" style={{ width: "100%", padding: "12px 16px", opacity: busy ? 0.6 : 1 }}>
                    {busy ? "Casting off…" : "Create my account"}
                </button>
            </form>

            <p style={{ marginTop: 14, textAlign: "center", color: "var(--bm-text-dim)", fontSize: 12, lineHeight: 1.5 }}>
                By creating an account you agree to our{" "}
                <Link data-testid="register-terms-link" className="bm-link" to="/terms">Terms of Service</Link>
                {" "}and{" "}
                <Link data-testid="register-privacy-link" className="bm-link" to="/privacy">Privacy Policy</Link>.
            </p>

            <div style={{ marginTop: 18, textAlign: "center", color: "var(--bm-text-muted)", fontSize: 13 }}>
                Already have an account?{" "}
                <Link data-testid="register-to-login-link" className="bm-link" to={nextPath ? `/login?next=${encodeURIComponent(nextPath)}${prefilledEmail ? `&email=${encodeURIComponent(prefilledEmail)}` : ""}` : "/login"}>Sign in</Link>
            </div>
        </AuthShell>
    );
}
